import { Component, signal, inject, AfterViewInit, isDevMode } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { filter } from 'rxjs/operators';
import { LayoutService } from '../layout.service';
import { MenuItem } from '../sidebar/menu-data';
import { MenuAccessService } from '../../core/permissions/menu-access.service';

@Component({
    selector: 'app-mobile-sidebar',
    standalone: true,
    imports: [CommonModule, RouterModule],
    template: `
    <div class="mobile-sidebar-wrapper">
        <div class="mobile-sidebar-header">
            <a routerLink="/" class="logo" aria-label="Belatz - Ir al inicio">
                <img src="assets/images/logos/Belatz-sm.webp" alt="Belatz" height="20">
            </a>
            <button type="button" class="btn-close btn-close-white" style="font-size: 0.8rem;" aria-label="Cerrar menú" (click)="layoutService.closeMenu()"></button>
        </div>

        <div class="mobile-sidebar-content" data-simplebar>
            <ul class="navbar-nav">
                @for (section of menuSections(); track section.id) {
                    <li class="menu-title">
                        <i [class]="section.icon + ' icon-bg me-2'"></i>
                        <span>{{ section.label }}</span>
                    </li>
                    
                    @for (item of section.children; track item.id) {
                        <li class="nav-item">
                            @if (hasChildren(item)) {
                                <a href="javascript:void(0)" 
                                   class="nav-link"
                                   [class.collapsed]="!isOpen(item.id)"
                                   (click)="toggleItem(item.id, $event)">
                                    <div class="d-flex align-items-center">
                                        <i class="ri-checkbox-blank-circle-fill dot-indicator me-3"></i>
                                        <span class="nav-label">{{ item.label }}</span>
                                    </div>
                                    <i class="ri-arrow-right-s-line menu-arrow"></i>
                                </a>
                                <div class="collapse menu-dropdown" 
                                     [class.show]="isOpen(item.id)"
                                     [@slideDown]="isOpen(item.id) ? 'open' : 'closed'">
                                    <ul class="nav nav-sm flex-column">
                                        @for (child of item.children!; track child.id) {
                                            <li class="nav-item">
                                                @if (hasChildren(child)) {
                                                    <a href="javascript:void(0)" 
                                                       class="nav-link"
                                                       [class.collapsed]="!isOpen(child.id)"
                                                       (click)="toggleItem(child.id, $event)">
                                                        {{ child.label }}
                                                    </a>
                                                    <div class="collapse menu-dropdown" 
                                                         [class.show]="isOpen(child.id)"
                                                         [@slideDown]="isOpen(child.id) ? 'open' : 'closed'">
                                                        <ul class="nav nav-sm flex-column">
                                                            @for (grandchild of child.children!; track grandchild.id) {
                                                                <li class="nav-item">
                                                                    <a [routerLink]="(grandchild.enConstruccion && !isDev) ? '/en-construccion' : (grandchild.route ?? '/')" 
                                                                       [queryParams]="(grandchild.enConstruccion && !isDev) ? { from: grandchild.route } : null"
                                                                       routerLinkActive="active" 
                                                                       (click)="layoutService.closeMenu()"
                                                                       class="nav-link">
                                                                        {{ grandchild.label }}
                                                                    </a>
                                                                </li>
                                                            }
                                                        </ul>
                                                    </div>
                                                } @else {
                                                    <a [routerLink]="(child.enConstruccion && !isDev) ? '/en-construccion' : (child.route ?? '/')" 
                                                       [queryParams]="(child.enConstruccion && !isDev) ? { from: child.route } : null"
                                                       routerLinkActive="active" 
                                                       (click)="layoutService.closeMenu()"
                                                       class="nav-link">
                                                        {{ child.label }}
                                                    </a>
                                                }
                                            </li>
                                        }
                                    </ul>
                                </div>
                            } @else {
                                <a [routerLink]="(item.enConstruccion && !isDev) ? '/en-construccion' : (item.route ?? '/')" 
                                   [queryParams]="(item.enConstruccion && !isDev) ? { from: item.route } : null"
                                   routerLinkActive="active" 
                                   (click)="layoutService.closeMenu()"
                                   class="nav-link">
                                    <div class="d-flex align-items-center">
                                        <i class="ri-checkbox-blank-circle-fill dot-indicator me-3"></i>
                                        <span class="nav-label">{{ item.label }}</span>
                                    </div>
                                </a>
                            }
                        </li>
                    }
                }
            </ul>
        </div>
    </div>
    <div class="mobile-vertical-overlay" (click)="layoutService.closeMenu()"></div>
  `,
    styles: [`
    :host {
        display: block;
    }

    .mobile-sidebar-wrapper {
        position: fixed;
        left: -100%;
        top: 0;
        bottom: 0;
        width: 100%;
        max-width: 260px;
        background: #405189;
        z-index: 2000;
        transition: left 0.3s ease-in-out;
        display: flex;
        flex-direction: column;
        box-shadow: 5px 0 15px rgba(0,0,0,0.1);
    }

    :host-context(body.vertical-sidebar-enable) .mobile-sidebar-wrapper {
        left: 0 !important;
    }

    .mobile-sidebar-header {
        padding: 0px 20px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: rgba(0,0,0,0.1);
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .mobile-sidebar-content {
        flex: 1;
        overflow-y: auto;
        padding: 10px 0;
    }

    .navbar-nav {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .menu-title {
        color: rgba(255, 255, 255, 0.45);
        padding: 20px 20px 10px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        font-weight: 700;
        display: flex;
        align-items: center;

        .icon-bg {
            font-size: 14px;
            color: #fff;
            opacity: 0.6;
        }
    }

    .nav-link {
        color: rgba(255, 255, 255, 0.75) !important;
        padding: 12px 20px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 13.5px;
        text-decoration: none;
        transition: all 0.2s;

        &:hover, &.active {
            color: #fff !important;
            background: rgba(255, 255, 255, 0.08);

            .dot-indicator {
                color: #00bef0; /* Color cyan para resaltar el activo */
                transform: scale(1.2);
            }
        }

        .dot-indicator {
            font-size: 6px;
            color: rgba(255,255,255,0.2);
            transition: all 0.2s;
        }

        .menu-arrow {
            font-size: 14px;
            transition: transform 0.2s;
            opacity: 0.5;
        }

        &:not(.collapsed) .menu-arrow {
            transform: rotate(90deg);
            opacity: 1;
        }
    }

    .menu-dropdown {
        background: rgba(0, 0, 0, 0.08);
        
        .nav-link {
            padding-left: 45px;
            font-size: 13px;
        }
        
        .menu-dropdown .nav-link {
            padding-left: 60px;
        }
    }

    .mobile-vertical-overlay {
        display: none;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1999;
        backdrop-filter: blur(2px);
    }

    :host-context(body.vertical-sidebar-enable) .mobile-vertical-overlay {
        display: block !important;
    }
  `],
    animations: [
        trigger('slideDown', [
            state('closed', style({ height: '0px', overflow: 'hidden', opacity: 0 })),
            state('open', style({ height: '*', overflow: 'hidden', opacity: 1 })),
            transition('closed <=> open', animate('250ms ease-in-out')),
        ])
    ]
})
export class MobileSidebarComponent implements AfterViewInit {
    layoutService = inject(LayoutService);
    private router  = inject(Router);
    private menuAccess = inject(MenuAccessService);

    openItemIds = signal(new Set<string>());
    isDev = isDevMode();

    readonly menuSections = this.menuAccess.sections;

    ngAfterViewInit() {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.openRelatedItems();
        });
        this.openRelatedItems();
    }

    private openRelatedItems() {
        let url = this.router.url;
        if (url.startsWith('/en-construccion')) {
            const urlTree = this.router.parseUrl(url);
            const fromUrl = urlTree.queryParams['from'];
            if (fromUrl) {
                url = fromUrl;
            }
        }
        this.menuSections().forEach(section => {
            section.children.forEach(item => {
                if (this.containsRoute(item, url)) {
                    this.openItemIds.update(set => {
                        const next = new Set(set);
                        next.add(item.id);
                        return next;
                    });
                }
            });
        });
    }

    private containsRoute(item: MenuItem, url: string): boolean {
        if (item.route === url) return true;
        if (item.children) {
            return item.children.some(child => this.containsRoute(child, url));
        }
        return false;
    }

    toggleItem(id: string, event: Event): void {
        event.preventDefault();
        this.openItemIds.update(set => {
            const next = new Set(set);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    isOpen(id: string): boolean {
        return this.openItemIds().has(id);
    }

    hasChildren(item: MenuItem): boolean {
        return !!(item.children && item.children.length > 0);
    }
}
